<template>
  <Popover v-model:open="isOpen">
    <PopoverTrigger
      class="flex items-center gap-1.5 text-xs hover:text-gray-700 dark:hover:text-neutral-300"
    >
      <FileDownIcon class="h-3.5 w-3.5" />
      <span>{{ t('SaveChat') }}</span>
    </PopoverTrigger>
    <PopoverContent
      class="w-[320px] p-0"
      side="bottom"
      align="end"
      :side-offset="8"
    >
      <!-- Header -->
      <div class="flex items-center justify-between border-b border-border px-4 py-2">
        <h3 class="text-base font-medium">
          {{ chatExportPopover.messageUuid.value ? t('SaveMessageAsPdf') : t('SaveChatAsPdf') }}
        </h3>
        <button
          class="text-gray-500 hover:text-gray-700 dark:text-neutral-400 dark:hover:text-neutral-300"
          @click="isOpen = false"
        >
          <XIcon class="h-4 w-4" />
        </button>
      </div>

      <!-- Info Banner - Now permanent and better integrated -->
      <div class="border-b border-gray-200 bg-blue-50 px-4 py-3 dark:bg-blue-950">
        <div class="flex items-center gap-2">
          <InfoIcon class="h-4 w-4 shrink-0 text-blue-700 dark:text-blue-300" />
          <span class="text-sm text-blue-700 dark:text-blue-300">
            {{
              chatExportPopover.messageUuid.value
                ? t('ExportingSingleMessage')
                : t('ExportingEntireChat')
            }}
          </span>
        </div>
      </div>

      <div class="p-4">
        <!-- Options -->
        <div class="space-y-4">
          <div class="space-y-2">
            <div class="flex items-center space-x-2">
              <input
                id="attach-to-case"
                v-model="attachToCase"
                type="checkbox"
                class="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
              />
              <label
                for="attach-to-case"
                class="text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                {{ t('AttachToCase') }}
              </label>
            </div>
            <p class="text-xs text-gray-500">
              {{ t('AttachToCaseDescription') }}
            </p>
          </div>

          <!-- Action Button -->
          <button
            class="w-full rounded-md bg-purple-600 px-3 py-2 text-sm font-medium text-white shadow-sm
              hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
            :disabled="isGeneratingPdf"
            @click="generatePdf"
          >
            <div class="flex items-center justify-center gap-2">
              <LoaderCircle
                v-if="isGeneratingPdf"
                class="h-4 w-4 animate-spin"
              />
              {{ t('GeneratePdf') }}
            </div>
          </button>
        </div>
      </div>
    </PopoverContent>
  </Popover>
</template>

<script setup lang="ts">
  import { ref, inject, computed, type Ref } from 'vue'
  import { XIcon, FileDownIcon, LoaderCircle, InfoIcon } from 'lucide-vue-next'
  import Popover from '@/components/ui/popover/Popover.vue'
  import PopoverTrigger from '@/components/ui/popover/PopoverTrigger.vue'
  import PopoverContent from '@/components/ui/popover/PopoverContent.vue'
  import { useTranslation } from '@/composables/useTranslation'
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore'
  import { AiChatSessionsService } from '@/types/generated/services.gen'
  import { saveSuccessToast, genericErrorToast } from '@/lib/toasts'
  import * as Sentry from '@sentry/vue'
  import { useChatExportPopover } from '@/composables/useChatExportPopover'

  const props = defineProps<{
    caseData: any | null
  }>()

  const { t } = useTranslation({
    en: {
      SaveChat: 'Save Chat',
      SaveChatAsPdf: 'Save chat as PDF',
      SaveMessageAsPdf: 'Save message as PDF',
      AttachToCase: 'Attach to case',
      AttachToCaseDescription: 'The PDF will be saved as a document in the current case.',
      Cancel: 'Cancel',
      GeneratePdf: 'Generate PDF',
      ExportingSingleMessage: 'You are exporting a single message from this chat.',
      ExportingEntireChat: 'You are exporting the entire chat conversation.'
    },
    de: {
      SaveChat: 'Chat speichern',
      SaveChatAsPdf: 'Chat als PDF speichern',
      SaveMessageAsPdf: 'Nachricht als PDF speichern',
      AttachToCase: 'An Anfrage anhängen',
      AttachToCaseDescription: 'Die PDF wird als Dokument in der aktuellen Anfrage gespeichert.',
      Cancel: 'Abbrechen',
      GeneratePdf: 'PDF generieren',
      ExportingSingleMessage: 'Sie exportieren eine einzelne Nachricht aus diesem Chat.',
      ExportingEntireChat: 'Sie exportieren die gesamte Chat-Konversation.'
    }
  })

  const caseStore = useCaseStore()
  const activeChatSession = inject<Ref<any | null>>('activeChatSession', ref(null))

  // Use the chat export popover composable
  const chatExportPopover = useChatExportPopover()

  // Create computed properties to bridge between the composable state and the component
  const isOpen = computed({
    get: () => chatExportPopover.isOpen.value,
    set: (value) => (value ? chatExportPopover.openPopover() : chatExportPopover.closePopover())
  })

  const attachToCase = computed({
    get: () => chatExportPopover.attachToCase.value,
    set: (value) => chatExportPopover.setAttachToCase(value)
  })

  const isGeneratingPdf = computed({
    get: () => chatExportPopover.isGeneratingPdf.value,
    set: (value) => chatExportPopover.setIsGeneratingPdf(value)
  })

  const generatePdf = async () => {
    try {
      chatExportPopover.setIsGeneratingPdf(true)

      // Get the chat session ID
      const sessionId = activeChatSession.value?.uuid
      if (!sessionId) {
        genericErrorToast()
        return
      }

      // Use the generated service method
      const response = await AiChatSessionsService.aiChatSessionsExportPdfCreate({
        uuid: sessionId,
        requestBody: {
          attach_to_case: chatExportPopover.attachToCase.value,
          message_uuid: chatExportPopover.messageUuid.value // Use the message UUID from the composable
        }
      })

      if (response.success) {
        // Generate appropriate filename based on whether we're exporting a single message or the entire chat
        const filenameSuffix = chatExportPopover.messageUuid.value
          ? `-message-${chatExportPopover.messageUuid.value.substring(0, 8)}`
          : ''

        if (chatExportPopover.attachToCase.value) {
          // For documents attached to case, use the URL
          if (response.url) {
            const link = document.createElement('a')
            link.href = response.url
            link.download = `chat-${sessionId.substring(0, 8)}${filenameSuffix}.pdf`
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)

            // Refresh case data to show the new document
            if (props.caseData?.uuid) {
              await caseStore.fetchCaseWithErrorHandling(props.caseData.uuid)
            }
          }
        } else {
          // For temporary files, use the content_base64 data
          if (response.content_base64) {
            // Convert base64 string to binary data using atob()
            const binaryString = atob(response.content_base64)

            // Create a Uint8Array from the binary string
            const bytes = new Uint8Array(binaryString.length)
            for (let i = 0; i < binaryString.length; i++) {
              bytes[i] = binaryString.charCodeAt(i)
            }

            const blob = new Blob([bytes], {
              type: 'application/pdf'
            })

            // Create a download link
            const url = URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download =
              response.filename || `chat-${sessionId.substring(0, 8)}${filenameSuffix}.pdf`
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            URL.revokeObjectURL(url)
          }
        }

        saveSuccessToast()
      } else {
        genericErrorToast()
      }
    } catch (error) {
      console.error('Failed to generate PDF:', error)
      genericErrorToast()
      Sentry.captureException(error)
    } finally {
      chatExportPopover.setIsGeneratingPdf(false)
      chatExportPopover.closePopover()
    }
  }
</script>
